package com.sweetlime.dungeonamigo.presentation.features.session

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Casino
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Condition
import com.sweetlime.dungeonamigo.model.RollEvent
import com.sweetlime.dungeonamigo.model.SessionParticipant
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceRollerSheet
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceViewModel
import com.sweetlime.dungeonamigo.presentation.ui.components.CharacterPortrait
import com.sweetlime.dungeonamigo.presentation.ui.components.PortraitSize
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import org.koin.compose.koinInject

data class PlayerSessionScreen(val roomId: String) : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: SessionViewModel = koinInject()
        val diceVm: DiceViewModel = koinInject()
        val state by vm.sessionState.collectAsState()
        var showDiceSheet by remember { mutableStateOf(false) }
        var showSetHpDialog by remember { mutableStateOf(false) }
        var showAddConditionDialog by remember { mutableStateOf(false) }

        // Auto-post dice rolls to session feed; clean up session on dispose (back button)
        DisposableEffect(Unit) {
            diceVm.onRollCompleted = { roll, label ->
                if (vm.sessionState.value is SessionState.InSession) {
                    vm.postRollToFeed(roll, label)
                }
            }
            onDispose {
                diceVm.onRollCompleted = null
                vm.leaveRoom()
            }
        }

        // Pop back if session ends via ViewModel (not via back button)
        val shouldPop = state is SessionState.Idle || state is SessionState.Error
        LaunchedEffect(shouldPop) {
            if (shouldPop) navigator.pop()
        }

        val session = state as? SessionState.InSession ?: return
        val me = session.myParticipant

        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(BrandColors.Background),
        ) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 80.dp),
            ) {
                // ── My Character Section ─────────────────────────────────────
                item {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(BrandColors.Surface)
                            .padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column {
                                Text(
                                    me?.characterName ?: me?.displayName ?: "—",
                                    color = BrandColors.TextPrimary,
                                    fontSize = 20.sp,
                                    fontWeight = FontWeight.Bold,
                                )
                                if (me != null && me.characterClass.isNotBlank()) {
                                    Text(
                                        "${me.characterClass} Lv.${me.characterLevel}",
                                        color = BrandColors.TextSecondary,
                                        fontSize = 13.sp,
                                    )
                                }
                            }
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                StatBadge("AC", me?.armorClass?.toString() ?: "—", BrandColors.SpellBlue)
                                StatBadge("Init", me?.let {
                                    val init = it.initiativeRoll
                                    if (init > 0) "+$init" else "$init"
                                } ?: "—", BrandColors.Parchment)
                            }
                        }

                        // HP Tracker
                        if (me != null) {
                            HpTracker(
                                current = me.currentHp,
                                max = me.maxHp,
                                temp = me.temporaryHp,
                                onDecrement = { vm.updateMyHp((me.currentHp - 1).coerceAtLeast(0), me.temporaryHp) },
                                onIncrement = { vm.updateMyHp((me.currentHp + 1).coerceAtMost(me.maxHp), me.temporaryHp) },
                                onLongPress = { showSetHpDialog = true },
                                onAddTemp = { vm.updateMyHp(me.currentHp, me.temporaryHp + 1) },
                            )
                        }

                        // Condition chips
                        if (me != null) {
                            ConditionRow(
                                conditions = me.conditions,
                                onRemove = { cond ->
                                    vm.updateMyConditions(me.conditions.filter { it != cond })
                                },
                                onAdd = { showAddConditionDialog = true },
                            )
                        }

                        // Roll initiative button
                        OutlinedButton(
                            onClick = vm::rollInitiative,
                            modifier = Modifier.fillMaxWidth(),
                            border = BorderStroke(1.dp, BrandColors.Parchment.copy(alpha = 0.4f)),
                            colors = ButtonColors(
                                containerColor = Color.Transparent,
                                contentColor = BrandColors.Parchment,
                                disabledContainerColor = Color.Transparent,
                                disabledContentColor = BrandColors.TextSecondary,
                            ),
                            shape = RoundedCornerShape(8.dp),
                        ) {
                            Text("🎲 Roll Initiative", fontSize = 13.sp)
                        }
                    }
                }

                // ── Party Overview ───────────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text("The Party", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                        RoomCodeBadge(session.room.roomCode)
                    }
                }

                items(session.participants.filter { !it.isDm && it.userId != me?.userId }) { participant ->
                    PartyMemberCard(
                        participant = participant,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                    )
                }

                // ── Roll Feed ────────────────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Text("Roll Feed", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                        Text("🎲", fontSize = 18.sp)
                    }
                }

                items(session.rollFeed.take(10)) { roll ->
                    RollFeedRow(
                        roll = roll,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 2.dp),
                    )
                }

                if (session.rollFeed.isEmpty()) {
                    item {
                        Text(
                            "No rolls yet",
                            color = BrandColors.TextSecondary,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(horizontal = 16.dp),
                        )
                    }
                }
            }

            // Dice FAB
            FloatingActionButton(
                onClick = { showDiceSheet = true },
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(16.dp),
                containerColor = BrandColors.Ember,
            ) {
                Icon(Icons.Filled.Casino, "Roll Dice", tint = BrandColors.TextPrimary)
            }
        }

        if (showDiceSheet) {
            DiceRollerSheet(vm = diceVm, onDismiss = { showDiceSheet = false })
        }

        if (showSetHpDialog && me != null) {
            SetHpDialog(
                current = me.currentHp,
                max = me.maxHp,
                temp = me.temporaryHp,
                onApply = { newHp, newTemp -> vm.updateMyHp(newHp, newTemp) },
                onDismiss = { showSetHpDialog = false },
            )
        }

        if (showAddConditionDialog && me != null) {
            ConditionPickerDialog(
                activeConditions = me.conditions,
                onApply = { vm.updateMyConditions(it) },
                onDismiss = { showAddConditionDialog = false },
            )
        }
    }
}

// ── Shared composables used by both player and DM screens ────────────────────

@Composable
fun HpTracker(
    current: Int,
    max: Int,
    temp: Int,
    onDecrement: () -> Unit,
    onIncrement: () -> Unit,
    onLongPress: () -> Unit,
    onAddTemp: () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(52.dp)
                    .clip(CircleShape)
                    .background(BrandColors.SurfaceLight)
                    .combinedClickable(onClick = onDecrement, onLongClick = onLongPress),
                contentAlignment = Alignment.Center,
            ) {
                Text("−", color = BrandColors.Ember, fontSize = 28.sp, fontWeight = FontWeight.Bold)
            }

            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Row(
                    verticalAlignment = Alignment.Bottom,
                    horizontalArrangement = Arrangement.Center,
                ) {
                    Text(
                        "$current",
                        color = hpColor(current, max),
                        fontSize = 40.sp,
                        fontWeight = FontWeight.ExtraBold,
                        lineHeight = 40.sp,
                    )
                    Text(
                        "/$max",
                        color = BrandColors.TextSecondary,
                        fontSize = 18.sp,
                        modifier = Modifier.padding(bottom = 4.dp),
                    )
                }
                HpBar(current = current, max = max)
            }

            Box(
                modifier = Modifier
                    .size(52.dp)
                    .clip(CircleShape)
                    .background(BrandColors.SurfaceLight)
                    .combinedClickable(onClick = onIncrement, onLongClick = onLongPress),
                contentAlignment = Alignment.Center,
            ) {
                Text("+", color = BrandColors.Success, fontSize = 28.sp, fontWeight = FontWeight.Bold)
            }
        }

        // Temp HP row
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Text("Temp HP:", color = BrandColors.TextSecondary, fontSize = 12.sp)
            if (temp > 0) {
                Text(
                    "$temp",
                    color = BrandColors.SpellBlue,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Bold,
                )
            }
            Box(
                modifier = Modifier
                    .clip(CircleShape)
                    .background(BrandColors.SurfaceLight)
                    .clickable(onClick = onAddTemp)
                    .padding(4.dp),
            ) {
                Icon(Icons.Filled.Add, "Add temp HP", tint = BrandColors.SpellBlue, modifier = Modifier.size(14.dp))
            }
            if (temp > 0) {
                Text(
                    "−",
                    color = BrandColors.TextSecondary,
                    fontSize = 12.sp,
                    modifier = Modifier
                        .clip(CircleShape)
                        .background(BrandColors.SurfaceLight)
                        .clickable { /* handled by decrement long-press flow */ }
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                )
            }
        }
    }
}

@Composable
fun HpBar(current: Int, max: Int, modifier: Modifier = Modifier) {
    if (max <= 0) return
    val fraction = (current.toFloat() / max).coerceIn(0f, 1f)
    val color = hpColor(current, max)
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(8.dp)
            .clip(RoundedCornerShape(4.dp))
            .background(BrandColors.SurfaceLight),
    ) {
        Box(
            modifier = Modifier
                .fillMaxHeight()
                .fillMaxWidth(fraction)
                .clip(RoundedCornerShape(4.dp))
                .background(color),
        )
    }
}

private fun hpColor(current: Int, max: Int): Color {
    if (max <= 0) return BrandColors.HpFull
    return when {
        current.toFloat() / max > 0.5f -> BrandColors.HpFull
        current.toFloat() / max > 0.25f -> BrandColors.HpMid
        else -> BrandColors.HpLow
    }
}

@Composable
fun ConditionRow(
    conditions: List<String>,
    onRemove: (String) -> Unit,
    onAdd: () -> Unit,
) {
    if (conditions.isEmpty()) {
        // show just the add chip
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(16.dp))
                .background(BrandColors.SurfaceLight)
                .clickable(onClick = onAdd)
                .padding(horizontal = 10.dp, vertical = 4.dp),
        ) {
            Text("+ Add Condition", color = BrandColors.TextSecondary, fontSize = 12.sp)
        }
        return
    }
    Row(
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        conditions.forEach { condName ->
            val cond = Condition.entries.find { it.name == condName }
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(16.dp))
                    .background(BrandColors.Ember.copy(alpha = 0.15f))
                    .border(1.dp, BrandColors.Ember.copy(alpha = 0.5f), RoundedCornerShape(16.dp))
                    .clickable { onRemove(condName) }
                    .padding(horizontal = 8.dp, vertical = 3.dp),
            ) {
                Text(
                    "${cond?.emoji ?: ""}${cond?.displayName ?: condName}",
                    color = BrandColors.Ember,
                    fontSize = 11.sp,
                )
            }
        }
        Box(
            modifier = Modifier
                .clip(CircleShape)
                .background(BrandColors.SurfaceLight)
                .clickable(onClick = onAdd)
                .padding(4.dp),
        ) {
            Icon(Icons.Filled.Add, "Add condition", tint = BrandColors.TextSecondary, modifier = Modifier.size(14.dp))
        }
    }
}

@Composable
fun RoomCodeBadge(code: String) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(BrandColors.SurfaceLight)
            .border(1.dp, BrandColors.Parchment.copy(alpha = 0.4f), RoundedCornerShape(8.dp))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    ) {
        Text(
            code,
            color = BrandColors.Parchment,
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
            fontFamily = FontFamily.Monospace,
            letterSpacing = 4.sp,
        )
    }
}

@Composable
fun StatBadge(label: String, value: String, color: Color) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(BrandColors.SurfaceLight)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    ) {
        Text(value, color = color, fontSize = 16.sp, fontWeight = FontWeight.Bold)
        Text(label, color = BrandColors.TextSecondary, fontSize = 9.sp)
    }
}

@Composable
fun PartyMemberCard(
    participant: SessionParticipant,
    modifier: Modifier = Modifier,
    showDmControls: Boolean = false,
    onHpTap: (() -> Unit)? = null,
    onConditionAdd: (() -> Unit)? = null,
    onKick: (() -> Unit)? = null,
) {
    val hpPct = if (participant.maxHp > 0) participant.currentHp.toFloat() / participant.maxHp else 0f
    val lowHp = hpPct < 0.25f && participant.maxHp > 0

    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(BrandColors.Surface)
            .then(
                if (lowHp) Modifier.border(1.dp, BrandColors.Ember.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                else Modifier
            )
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    if (participant.isDm) Text("👑", fontSize = 12.sp)
                    Text(
                        participant.characterName.ifBlank { participant.displayName },
                        color = BrandColors.TextPrimary,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 14.sp,
                    )
                }
                if (participant.characterClass.isNotBlank()) {
                    Text(
                        "${participant.characterClass} Lv.${participant.characterLevel}",
                        color = BrandColors.TextSecondary,
                        fontSize = 11.sp,
                    )
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "AC ${participant.armorClass}",
                    color = BrandColors.SpellBlue,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    "Init ${participant.initiativeRoll}",
                    color = BrandColors.Parchment,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                if (showDmControls && onKick != null && !participant.isDm) {
                    TextButton(
                        onClick = onKick,
                        contentPadding = PaddingValues(horizontal = 4.dp),
                    ) {
                        Text("✕", color = BrandColors.Ember, fontSize = 12.sp)
                    }
                }
            }
        }

        // HP bar
        val hpClick = if (showDmControls && onHpTap != null) Modifier.clickable(onClick = onHpTap) else Modifier
        Row(
            modifier = Modifier.fillMaxWidth().then(hpClick),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            HpBar(participant.currentHp, participant.maxHp, Modifier.weight(1f))
            Text(
                "${participant.currentHp}/${participant.maxHp}",
                color = BrandColors.TextSecondary,
                fontSize = 11.sp,
            )
        }

        // Conditions (read-only for other players; DM can add)
        if (participant.conditions.isNotEmpty() || (showDmControls && onConditionAdd != null)) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                participant.conditions.forEach { condName ->
                    val cond = Condition.entries.find { it.name == condName }
                    Text(
                        "${cond?.emoji ?: ""}${cond?.displayName ?: condName}",
                        color = BrandColors.Ember,
                        fontSize = 10.sp,
                        modifier = Modifier
                            .clip(RoundedCornerShape(10.dp))
                            .background(BrandColors.Ember.copy(alpha = 0.12f))
                            .padding(horizontal = 6.dp, vertical = 2.dp),
                    )
                }
                if (showDmControls && onConditionAdd != null) {
                    Box(
                        modifier = Modifier
                            .clip(CircleShape)
                            .background(BrandColors.SurfaceLight)
                            .clickable(onClick = onConditionAdd)
                            .padding(3.dp),
                    ) {
                        Icon(Icons.Filled.Add, "Add condition", tint = BrandColors.TextSecondary, modifier = Modifier.size(12.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun RollFeedRow(roll: RollEvent, modifier: Modifier = Modifier) {
    val bgColor = when {
        roll.isCrit -> BrandColors.Parchment.copy(alpha = 0.12f)
        roll.isCritFail -> BrandColors.Ember.copy(alpha = 0.10f)
        else -> BrandColors.Surface
    }
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(bgColor)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            val name = roll.characterName.ifBlank { roll.displayName }
            if (name.isNotBlank()) {
                Text(name, color = BrandColors.TextSecondary, fontSize = 11.sp)
            }
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
                if (roll.label.isNotBlank()) {
                    Text(roll.label, color = BrandColors.TextPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
                if (roll.expression.isNotBlank()) {
                    Text("(${roll.expression})", color = BrandColors.TextSecondary, fontSize = 11.sp)
                }
            }
        }
        if (roll.total > 0) {
            Text(
                "${roll.total}",
                color = when {
                    roll.isCrit -> BrandColors.Parchment
                    roll.isCritFail -> BrandColors.Ember
                    else -> BrandColors.TextPrimary
                },
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
            )
        }
    }
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

@Composable
fun SetHpDialog(
    current: Int,
    max: Int,
    temp: Int,
    onApply: (newHp: Int, newTemp: Int) -> Unit,
    onDismiss: () -> Unit,
) {
    var mode by remember { mutableStateOf(0) } // 0=set, 1=damage, 2=heal
    var input by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        title = { Text("Adjust HP", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text("Current: $current / $max", color = BrandColors.TextSecondary, fontSize = 13.sp)
                // Mode tabs
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    listOf("Set to", "Damage", "Heal").forEachIndexed { i, label ->
                        val selected = mode == i
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .background(if (selected) BrandColors.Parchment else BrandColors.SurfaceLight)
                                .clickable { mode = i; input = "" }
                                .padding(horizontal = 10.dp, vertical = 6.dp),
                        ) {
                            Text(
                                label,
                                color = if (selected) BrandColors.Background else BrandColors.TextSecondary,
                                fontSize = 12.sp,
                                fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                            )
                        }
                    }
                }
                OutlinedTextField(
                    value = input,
                    onValueChange = { v -> input = v.filter { it.isDigit() } },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    placeholder = { Text(if (mode == 0) "HP value" else "Amount", color = BrandColors.TextSecondary) },
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = BrandColors.Parchment,
                        unfocusedBorderColor = BrandColors.SurfaceLight,
                        focusedTextColor = BrandColors.TextPrimary,
                        unfocusedTextColor = BrandColors.TextPrimary,
                        cursorColor = BrandColors.Parchment,
                    ),
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                )
            }
        },
        confirmButton = {
            TextButton(onClick = {
                val value = input.toIntOrNull() ?: return@TextButton
                val newHp = when (mode) {
                    0 -> value.coerceIn(0, max)
                    1 -> (current - value).coerceAtLeast(0)
                    2 -> (current + value).coerceAtMost(max)
                    else -> current
                }
                onApply(newHp, temp)
                onDismiss()
            }) {
                Text("Apply", color = BrandColors.Parchment, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel", color = BrandColors.TextSecondary)
            }
        },
    )
}

@Composable
fun ConditionPickerDialog(
    activeConditions: List<String>,
    onApply: (List<String>) -> Unit,
    onDismiss: () -> Unit,
) {
    var selected by remember { mutableStateOf(activeConditions.toSet()) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        title = { Text("Conditions", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Condition.entries.forEach { cond ->
                    val isActive = cond.name in selected
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(if (isActive) BrandColors.Ember.copy(alpha = 0.12f) else BrandColors.SurfaceLight)
                            .clickable {
                                selected = if (isActive) selected - cond.name else selected + cond.name
                            }
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(cond.emoji, fontSize = 16.sp)
                        Column(Modifier.weight(1f)) {
                            Text(cond.displayName, color = if (isActive) BrandColors.Ember else BrandColors.TextPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                            Text(cond.shortDescription, color = BrandColors.TextSecondary, fontSize = 11.sp)
                        }
                        if (isActive) {
                            Text("✓", color = BrandColors.Ember, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = {
                onApply(selected.toList())
                onDismiss()
            }) {
                Text("Apply", color = BrandColors.Parchment, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel", color = BrandColors.TextSecondary)
            }
        },
    )
}
